// Cole's LMS method — forward/inverse transforms, linear interpolation
// between tabulated ages. See growth-spec §2.

export type LMSRow = { age_yr: number; L: number; M: number; S: number };
export type Sex = 'M' | 'F';
export type Metric = 'weight' | 'length' | 'head_circumference';
export type ReferenceId = 'who' | 'nl3' | 'nl4' | 'nl5';

const L_ZERO_THRESHOLD = 1e-7;

export type LMS = { L: number; M: number; S: number };

/** Linear-interpolate L, M, S to the requested age. Returns null when the
 *  age is outside the tabulated range — callers should surface that, not
 *  extrapolate. */
export function interpolateLMS(rows: readonly LMSRow[], ageYr: number): LMS | null {
  if (!rows.length) return null;
  if (ageYr < rows[0].age_yr || ageYr > rows[rows.length - 1].age_yr) return null;

  // Binary search the bracket.
  let lo = 0;
  let hi = rows.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (rows[mid].age_yr <= ageYr) lo = mid;
    else hi = mid;
  }
  const a = rows[lo];
  const b = rows[hi];
  if (a.age_yr === ageYr) return { L: a.L, M: a.M, S: a.S };
  if (b.age_yr === ageYr) return { L: b.L, M: b.M, S: b.S };
  const frac = (ageYr - a.age_yr) / (b.age_yr - a.age_yr);
  return {
    L: a.L + frac * (b.L - a.L),
    M: a.M + frac * (b.M - a.M),
    S: a.S + frac * (b.S - a.S),
  };
}

/** SDS (z-score) for a measurement at a given age. */
export function valueToSDS(value: number, { L, M, S }: LMS): number {
  if (Math.abs(L) < L_ZERO_THRESHOLD) {
    return Math.log(value / M) / S;
  }
  return (Math.pow(value / M, L) - 1) / (L * S);
}

/** Inverse: measurement for a given SDS. Returns null when (1 + L·S·Z) is
 *  non-positive (extreme tails for L > 0). */
export function sdsToValue(z: number, { L, M, S }: LMS): number | null {
  if (Math.abs(L) < L_ZERO_THRESHOLD) {
    return M * Math.exp(S * z);
  }
  const base = 1 + L * S * z;
  if (base <= 0) return null;
  return M * Math.pow(base, 1 / L);
}

/** Standard-normal CDF. Abramowitz & Stegun 7.1.26 approximation. */
export function normalCdf(z: number): number {
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const d = 0.3989422804014327 * Math.exp(-(z * z) / 2);
  const p =
    d * t * (0.319381530 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
  return z >= 0 ? 1 - p : p;
}

/** Sample LMS at a set of ages, evaluating measurement at each requested
 *  SDS line. Used to draw percentile bands on the chart. */
export function sampleBands(
  rows: readonly LMSRow[],
  ageMinYr: number,
  ageMaxYr: number,
  steps: number,
  sdsLines: readonly number[],
): { ageYr: number; values: (number | null)[] }[] {
  if (ageMaxYr <= ageMinYr || steps <= 1) return [];
  const a0 = Math.max(ageMinYr, rows[0].age_yr);
  const a1 = Math.min(ageMaxYr, rows[rows.length - 1].age_yr);
  if (a1 <= a0) return [];
  const out: { ageYr: number; values: (number | null)[] }[] = [];
  for (let i = 0; i < steps; i++) {
    const ageYr = a0 + (a1 - a0) * (i / (steps - 1));
    const lms = interpolateLMS(rows, ageYr);
    if (!lms) continue;
    out.push({ ageYr, values: sdsLines.map((z) => sdsToValue(z, lms)) });
  }
  return out;
}

/** Standard percentile band SDS lines (TNO convention). */
export const STANDARD_BAND_SDS = [-2.5, -2, -1, 0, 1, 2, 2.5] as const;
